import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@material-ui/core';
import { Fullscreen } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetHeader } from '../WidgetHeader';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { PwiBand, parsePwiResults } from './pwi-results';
import { PwiExpandedDialog } from './PwiExpandedDialog';

export type PwiWidgetData = {
  name: string;
  results: string;
};

const BAND_LABELS: Record<PwiBand, string> = {
  normal: 'Normal',
  challenged: 'Challenged',
  'high-risk': 'High-risk',
};

const useStyles = makeStyles(() =>
  createStyles({
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '0 20px 16px 20px',
    },
    stages: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 8,
    },
    stage: {
      borderRadius: 6,
      background: '#F7F7F7',
      padding: '10px 8px',
      textAlign: 'center',
    },
    stageLabel: {
      fontSize: '13px',
      fontWeight: 600,
      color: '#646464',
    },
    stageValue: {
      fontSize: '32px',
      fontWeight: 800,
      color: '#343434',
      lineHeight: '40px',
    },
    muted: {
      fontSize: '12px',
      color: '#808080',
    },
    sectionTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: '#646464',
      marginTop: 14,
      marginBottom: 4,
    },
    row: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) repeat(3, minmax(0, 1fr))',
      gap: 6,
      fontSize: '12px',
      padding: '3px 0',
      borderBottom: '1px solid #F0F0F0',
    },
    cell: {
      fontSize: '12px',
      color: '#343434',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
    nameCell: {
      fontSize: '12px',
      color: '#343434',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

const formatScore = (value: number | null) => (value === null ? '–' : Math.round(value).toString());

const formatChange = (value: number | null) => {
  if (value === null) return '–';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

export function PwiWidget({ widget }: { widget: PwiWidgetData }) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const results = parsePwiResults(widget.results);
  const [expanded, setExpanded] = useState(false);

  const bandColour = (band: PwiBand | null) => {
    if (band === 'normal') return theme.alert.green.dark;
    if (band === 'challenged') return theme.alert.yellow.dark;
    if (band === 'high-risk') return theme.alert.red.dark;
    return '#808080';
  };

  if (!results || !results.configured) {
    return (
      <WidgetContainer>
        <WidgetHeader title={widget.name} subTitle='Personal Wellbeing Index' type='Wellbeing' />
        <Box className={classes.content}>
          <Typography className={classes.muted}>Choose a task and its questions to see results.</Typography>
        </Box>
      </WidgetContainer>
    );
  }

  const separateQuestions = results.questions.filter((question) => question.role === 'separate');

  return (
    <WidgetContainer>
      <WidgetHeader
        title={widget.name}
        subTitle={results.rollup ? 'Personal Wellbeing Index · all sub-accounts' : 'Personal Wellbeing Index'}
        type='Wellbeing'
      >
        <IconButton
          size='small'
          onClick={() => setExpanded(true)}
          title='Expand View'
          aria-label='Expand view'
          style={{
            padding: '4px',
            border: `1px solid ${theme.color.grey.neutral200}`,
            borderRadius: '4px',
            backgroundColor: 'white',
            alignSelf: 'flex-start',
            marginRight: '26px',
          }}
        >
          <Fullscreen style={{ fontSize: '20px', color: theme.color.grey.neutral300 }} />
        </IconButton>
      </WidgetHeader>
      <PwiExpandedDialog open={expanded} onClose={() => setExpanded(false)} name={widget.name} results={results} />
      <Box className={classes.content}>
        <Box className={classes.stages}>
          {results.stages.map((stage) => (
            <Box key={stage.stage} className={classes.stage}>
              <Typography className={classes.stageLabel}>{stage.label}</Typography>
              <Typography className={classes.stageValue}>{formatScore(stage.index)}</Typography>
              <Typography style={{ fontSize: '12px', fontWeight: 600, color: bandColour(stage.band) }}>
                {stage.band ? BAND_LABELS[stage.band] : 'No surveys'}
              </Typography>
              <Typography className={classes.muted}>{`${stage.surveys} survey${
                stage.surveys === 1 ? '' : 's'
              }`}</Typography>
            </Box>
          ))}
        </Box>

        <Typography className={classes.muted} style={{ marginTop: 8 }}>
          {`Start → 6M ${formatChange(results.improvement.sixMonth)} · 6M → Closure ${formatChange(
            results.improvement.closure
          )} · Overall ${formatChange(results.improvement.overall)}`}
        </Typography>

        {separateQuestions.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>Shown separately</Typography>
            {separateQuestions.map((question) => (
              <Box key={question.label} className={classes.row}>
                <Typography className={classes.nameCell}>{question.label}</Typography>
                {question.averages.map((average, position) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Typography key={position} className={classes.cell}>
                    {formatScore(average)}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}

        {results.subAccounts.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>By sub-account</Typography>
            <Box className={classes.row}>
              <Typography className={classes.nameCell} style={{ color: '#808080' }}>
                Sub-account
              </Typography>
              {results.stages.map((stage) => (
                <Typography key={stage.stage} className={classes.cell} style={{ color: '#808080' }}>
                  {stage.label}
                </Typography>
              ))}
            </Box>
            {results.subAccounts.map((subAccount) => (
              <Box key={subAccount.name} className={classes.row}>
                <Typography className={classes.nameCell}>{subAccount.name}</Typography>
                {subAccount.stages.map((stage) => (
                  <Typography key={stage.stage} className={classes.cell}>
                    {`${formatScore(stage.index)} (${stage.surveys})`}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}

        {results.notReporting.length > 0 && (
          <Typography className={classes.muted} style={{ marginTop: 8 }}>
            {`Not reporting yet: ${results.notReporting.join(', ')}`}
          </Typography>
        )}
      </Box>
    </WidgetContainer>
  );
}
